import React, { useState, useRef } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import CreateRegularDeckModel from "./CreateRegularDeckModel";
import { ApiPost } from "../../helper/axiosFunctions";
import { useSelector } from "react-redux";
import { generateRandomId } from "../../helper/commonFunctions";
import DeckPreview from "./DeckPreview";

export default function CreateDeckModel({ open, setOpen, fetchData }) {
  const { t } = useTranslation();
  const user_data = useSelector((state) => state.profileData.profileData);
  const [addRegularDeckModel, setAddRegularDeckModel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationResponse, setValidationResponse] = useState(null);
  const [validatedFileContent, setValidatedFileContent] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [showDeckPreview, setShowDeckPreview] = useState(false);
  const [isCreatingDeck, setIsCreatingDeck] = useState(false);
  const [parsedDeckData, setParsedDeckData] = useState(null);
  const isCreatingRef = useRef(false);

  const handleFileInput = async (e) => {
    const files = e.target.files?.length ? e.target.files[0] : null;

    if (!files?.type.includes("text")) {
      toast.error(t("fileShouldBeTextDocument"));
      e.target.value = null;
      setSelectedFileName(null);
      return;
    }
    setSelectedFileName(files.name);
    try {
      setIsLoading(true);
      setValidationResponse(null);
      setValidatedFileContent(null);
      setShowDeckPreview(false);
      setParsedDeckData(null);
      const fileContent = await readFileContent(files);

      // Step 1: Only validate the file first (no parsing yet)
      const validateResponse = await ApiPost(
        `/contents/deck-text-format/validate`,
        { text: fileContent }
      );

      console.log('Validation response:', validateResponse);

      setValidationResponse(validateResponse?.data);

      // Step 2: If valid, show preview with Add Deck/Cancel buttons
      if (validateResponse && !validateResponse.errors) {
        toast.success(t("fileValidatedSuccessfully"));
        setValidatedFileContent(fileContent);
        setShowDeckPreview(true);
      } else if (validateResponse && validateResponse.errors) {
        // Step 3: If not valid, show validation errors
        toast.error(t("validationFailed", { errorCount: validateResponse.errors.length }));
      } else {
        toast.error(t("validationFailedGeneric"));
      }

    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t("invalidFileFormat"));
      } else if (error.response?.status === 401) {
        toast.error(t("authenticationFailed"));
      } else if (error.response?.status === 413) {
        toast.error(t("fileTooLarge"));
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error(t("requestTimedOut"));
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error(t("networkError"));
      } else {
        toast.error(t("errorProcessingFile"));
      }

      setValidationResponse({
        error: error.response?.data?.message || error.message || 'Unknown error occurred',
        status: error.response?.status
      });
    } finally {
      setIsLoading(false);
      e.target.value = null;
    }
  };

  // This function handles the final step: parse the validated file (which automatically creates the deck)
  const handleCreateDeck = async () => {
    // Prevent multiple simultaneous executions using both state and ref
    if (isCreatingDeck || isCreatingRef.current) {
      console.log('Deck creation already in progress, ignoring duplicate call');
      return;
    }

    if (!validatedFileContent) {
      toast.error(t("noValidatedFileContent"));
      return;
    }

    try {
      setIsCreatingDeck(true);
      isCreatingRef.current = true;

      // Parse the validated file content and create the deck (this API creates the deck automatically)
      const parseResponse = await ApiPost(
        `/contents/deck-text-format`,
        { text: validatedFileContent }
      );

      console.log('Parsed data:', parseResponse);
      setParsedDeckData(parseResponse);

      // Check if the deck was created successfully
      const responseData = parseResponse?.data || parseResponse;
      console.log('Response data:', responseData);

      // The API automatically creates the deck, so we just need to check if the response is valid
      if (responseData && (responseData.deckInfoResponse || responseData.deckId || responseData._id)) {
        toast.success(t("deckAddedSuccessfully"));

        // Reset all states after successful creation
        setShowDeckPreview(false);
        setValidationResponse(null);
        setValidatedFileContent(null);
        setSelectedFileName(null);
        setParsedDeckData(null);

        // Close the modal
        setOpen(false);

        // Refresh the data only once
        if (fetchData) {
          console.log('Calling fetchData to refresh the list...');
          await fetchData();
        }
      } else {
        console.error('Invalid response from deck creation:', parseResponse);
        throw new Error("Failed to create deck - invalid response");
      }

    } catch (error) {
      console.error("Error creating deck:", error);
      
      // Provide more specific error messages
      if (error.response?.status === 409) {
        toast.error(t("deckAlreadyExists"));
      } else if (error.response?.status === 400) {
        toast.error(t("invalidDeckData"));
      } else if (error.response?.status === 401) {
        toast.error(t("authenticationFailed"));
      } else if (error.response?.status === 413) {
        toast.error(t("deckTooLarge"));
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error(t("requestTimedOut"));
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error(t("networkError"));
      } else {
        toast.error(t("errorAddingDeck"));
      }
    } finally {
      setIsCreatingDeck(false);
      isCreatingRef.current = false;
    }
  };

  const handleCancelDeck = () => {
    setShowDeckPreview(false);
    setValidationResponse(null);
    setValidatedFileContent(null);
    setSelectedFileName(null);
    toast.info(t("deckCreationCancelled"));
  };



  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const handleAddRegularDeckBtn = () => {
    setAddRegularDeckModel(true);
  };

  // Function to render preview cards from parsed data
  const renderPreviewCard = (item, index) => {
    const questionImage = item?.question?.parts?.find(
      (part) => (part.contentType.includes("image") || part.contentType.includes("unknown"))
    )?.contentBody;
    const question = item?.question?.parts?.find(
      (part) => (part.contentType.includes("text"))
    )?.contentBody;
    const answer = item?.answers?.[0]?.parts?.find(
      (part) => (part.contentType.includes("text"))
    )?.contentBody;

    // Helper function to render hint content
    const renderHintContent = (part, idx) => {
      if (part.contentType.startsWith("text")) {
        return <div key={idx} className="text-gray-600" dangerouslySetInnerHTML={{ __html: part.contentBody }}></div>;
      }
      if (part.contentType.startsWith("image")) {
        return <img key={idx} src={part.contentBody} alt="Hint" className="max-w-full h-auto object-cover rounded-lg border text-sm my-2" />;
      }
      return null;
    };

    return (
      <div
        key={index + 1}
        className="rounded-xl border shadow-md p-4 bg-white flex flex-col gap-2"
      >
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-semibold">
              {item?.cardName || `${index + 1}-unnamed`}
            </h2>
          </div>
        </div>
        <div className='flex justify-between gap-2'>
          <p className='text-sm capitalize'>
            {t("answerType")}: {item?.answerType?.toLowerCase() || 'N/A'}
          </p>
          <p className='text-sm capitalize'>
            {t("complexity")}: {item?.complexity || 'N/A'}
          </p>
        </div>

        <hr className='my-2' />

        <div>
          <h3 className="font-semibold">{t("questions")}</h3>
          <div className='text-sm' dangerouslySetInnerHTML={{ __html: question || "N/A" }}></div>
        </div>

        {questionImage && (
          <div className="my-2">
            <img
              src={questionImage}
              alt={'questionImage' + (index + 1)}
              loading="lazy"
              className="max-w-full h-auto object-cover rounded-lg border text-sm"
            />
          </div>
        )}

        <div>
          <h3 className="font-semibold">{t("answer")}</h3>
          <div className='text-sm' dangerouslySetInnerHTML={{ __html: answer || "N/A" }}></div>
        </div>

        {item?.hints && item.hints.length > 0 && (
          <div>
            <h3 className="font-semibold">{t("Prompter")}</h3>
            <div className='text-sm space-y-1'>
              {item.hints.flatMap(hint => hint.parts.map(renderHintContent))}
            </div>
          </div>
        )}

        <div className="mt-auto pt-2 border-t w-full text-center cursor-pointer">
          <h6 className="base-text">{t("detail")}</h6>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Dialog
        open={open}
        as="div"
        className="relative z-30 focus:outline-none"
        onClose={() => {
          console.log('Modal closing, resetting states...');
          setOpen(false);
          // Reset all states when modal is closed
          setValidationResponse(null);
          setValidatedFileContent(null);
          setSelectedFileName(null);
          setShowDeckPreview(false);
          setIsCreatingDeck(false);
          isCreatingRef.current = false;
          setParsedDeckData(null);
        }}
      >
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 bg-black/35 backdrop-blur-sm">
            <DialogPanel
              transition
              className="w-full relative max-w-xl rounded-3xl bg-white duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
            >
              <IoMdClose
                className="absolute right-5 top-5 cursor-pointer"
                fontSize={20}
                onClick={() => {
                  console.log('Close button clicked, resetting states...');
                  setOpen(false);
                  // Reset all states when modal is closed
                  setValidationResponse(null);
                  setValidatedFileContent(null);
                  setSelectedFileName(null);
                  setShowDeckPreview(false);
                  setIsCreatingDeck(false);
                  isCreatingRef.current = false;
                  setParsedDeckData(null);
                }}
              />

              <div className="flex flex-col gap-10 p-5 ">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold">{t('newdeck')}</h3>
                </div>

                <div className="flex flex-wrap sm:flex-col items-center justify-between gap-3 ">
                  <div
                    className="bg-bluelight cursor-pointer border duration-300 hover:border-blue-500 w-full rounded-2xl flex flex-col text-center items-center justify-center h-72 xs:p-1"
                    onClick={handleAddRegularDeckBtn}
                  >

                    <img src="/assets/images/Deck.png" alt="Deck" />
                    <div>
                      <h2 className="regular-text">{t('regulardeck')}</h2>
                      <h3 className="base-text text-lightgray">
                        {t('createq')}
                      </h3>
                    </div>
                    <CreateRegularDeckModel
                      open={addRegularDeckModel}
                      setIsOpen={setAddRegularDeckModel}
                      onSuccess={() => { setOpen(false); fetchData(); }}
                    />
                  </div>
                  <label htmlFor="importFileInput" className={`bg-green-100 w-full rounded-2xl flex flex-col text-center items-center justify-center cursor-pointer h-52 xs:p-1 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <img src="/assets/images/class9.png" className="h-20" alt="Deck" />
                    <div>
                      <h2 className="regular-text">
                        {t('importFileAndCreateDeck')}
                      </h2>

                      <h3 className="base-text text-lightgray">
                        {t('createq')}
                      </h3>

                    </div>
                    <input
                      type="file"
                      id="importFileInput"
                      name="importFileInput"
                      accept=".txt"
                      onChange={handleFileInput}
                      disabled={isLoading}
                      hidden
                    />
                  </label>
                </div>

                {/* Loading Overlay */}
                {isLoading && (
                  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-800">{t("validatingFile")}</h3>
                        <p className="text-sm text-gray-600 mt-2">{t("pleaseWaitProcessing")}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Deck Preview Modal */}
                {showDeckPreview && validationResponse && (
                  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-hidden shadow-xl">
                      {/* Header */}
                      <div className="flex items-center justify-between p-4 border-b">
                        <h2 className="text-xl font-semibold text-gray-800">{t("sampleDeckPreview")}</h2>
                        <IoMdClose
                          className="cursor-pointer"
                          fontSize={20}
                          onClick={handleCancelDeck}
                        />
                      </div>

                      {/* Body */}
                      <div className="p-6 max-h-[70vh] overflow-y-auto [&::-webkit-scrollbar]:hidden">
                        <DeckPreview apiData={validationResponse} />
                      </div>

                      {/* Footer with Buttons */}
                      <div className="bg-secondarygray p-6 border-t border-gray-200">
                        <div className="flex gap-4">

                          <button
                            onClick={handleCancelDeck}
                            className="flex-1 bg-gray-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg"
                          >
                            {t('verifyAgain')}
                          </button>
                          <button
                            onClick={handleCreateDeck}
                            disabled={isCreatingDeck}
                            className="flex-1 bg-darkblue hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg"
                          >
                            {isCreatingDeck ? (
                              <div className="flex items-center justify-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {t('processingAndAdding')}
                              </div>
                            ) : (
                              t('addDeck')
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
